import React, { Suspense, lazy } from "react";
import { Routes, Route } from "react-router-dom";
import { usePageViewTracking } from "../firebase/usePageViewTracking";

// Eager (critical path)
import Homepage from "../pages/Homepage";
import NotFoundPage from "../components/layout/NotFoundPage";
import ErrorFallback from "../components/Common/ErrorFallback";
import { GuestRoute, ProtectedRoute, RoleRoute } from "./Guards";
import ContactPage from "../pages/ContactPage";

// Lazy (everything else)
const AboutPage          = lazy(() => import("../pages/AboutPage"));
const PrivacyPolicyPage  = lazy(() => import("../pages/PrivacyPolicyPage"));
const NewsPage           = lazy(() => import("../pages/NewsPage"));
const ParentsPage        = lazy(() => import("../pages/ParentsPage"));
const RomfordPage        = lazy(() => import("../pages/RomfordPage"));
const TilburyPage        = lazy(() => import("../pages/TilburyPage"));
const MealsPage          = lazy(() => import("../pages/MealsPage"));
const InquiriesAdminPage = lazy(() => import("../pages/InquiriesAdminPage"));
const FeesPage           = lazy(() => import("../pages/FeesPage"));

const EmailBannerPage   = lazy(() => import("../pages/EmailBanner"));

const LoginPage          = lazy(() => import("../pages/auth/LoginPage"));
const SignupPage         = lazy(() => import("../pages/auth/SignupPage"));
const ForgotPasswordPage = lazy(() => import("../pages/auth/ForgotPasswordPage"));
const SignedOutPage      = lazy(() => import("../pages/auth/SignedOutPage"));

const StaffMenuPage      = lazy(() => import("../pages/staff/StaffMenuPage"));
const DeleteAccountPage          = lazy(() => import("../pages/auth/DeleteAccountPage"));
const AccountDeletedPage          = lazy(() => import("../pages/auth/AccountDeletedPage"));
const UserManagementPage = lazy(() => import("../pages/staff/UserManagementPage"));
const WhyChooseEditor    = lazy(() => import("../pages/staff/cms/WhyChooseEditor"));
const ReviewsEditor      = lazy(() => import("../pages/staff/cms/ReviewsEditor"));

const NewsEditor         = lazy(() => import("../pages/staff/cms/NewsEditor"));
const ParentsEditor      = lazy(() => import("../pages/staff/cms/ParentsEditor"));
const MealsEditor        = lazy(() => import("../pages/staff/cms/MealsEditor"));
const FormsEditor        = lazy(() => import("../pages/staff/cms/FormsEditor"));
const FeesEditor         = lazy(() => import("../pages/staff/cms/FeesEditor"));
const FeaturesEditor     = lazy(() => import("../pages/staff/cms/FeaturesEditor"));
const AboutEditor        = lazy(() => import("../pages/staff/cms/AboutEditor"));

function AllRoutes() {
  usePageViewTracking();

  return (
    <div className="main-content">
      <Suspense fallback={<div className="p-4">Loading…</div>}>
        {/* If you have an ErrorBoundary component, wrap Suspense with it */}
        <Routes>
          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />

          {/* Public */}
          <Route path="/" element={<Homepage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/news" element={<NewsPage />} />
          <Route path="/parents" element={<ParentsPage />} />
          <Route path="/romford" element={<RomfordPage />} />
          <Route path="/tilbury" element={<TilburyPage />} />
          <Route path="/meals" element={<MealsPage />} />
          <Route path="/error" element={<ErrorFallback />} />
          <Route path="/fees" element={<FeesPage />} />
          <Route path="/email-banner" element={<EmailBannerPage />} />

          <Route path="/account/deleted" element={<AccountDeletedPage />} />

          {/* Auth (guests only) */}
          <Route element={<GuestRoute />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
          </Route>
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/signed-out" element={<SignedOutPage />} />

          {/* Staff area */}
          <Route element={<ProtectedRoute />}>
            <Route path="/staff" element={<StaffMenuPage />} />
            <Route path="/account/delete" element={<DeleteAccountPage />} />
            <Route element={<RoleRoute roles={['staff','editor','admin']} />}>
              <Route path="/staff/inquiries" element={<InquiriesAdminPage />} />
              <Route path="/staff/cms/why-choose" element={<WhyChooseEditor />} />
              <Route path="/staff/cms/reviews" element={<ReviewsEditor />} />
              <Route path="/staff/cms/news" element={<NewsEditor />} />
              <Route path="/staff/cms/parents" element={<ParentsEditor />} />
              <Route path="/staff/cms/meals/week1" element={<MealsEditor which="week1" />} />
              <Route path="/staff/cms/meals/week2" element={<MealsEditor which="week2" />} />
              <Route path="/staff/cms/forms" element={<FormsEditor />} />
              <Route path="/staff/cms/fees" element={<FeesEditor />} />
              <Route path="/staff/cms/features" element={<FeaturesEditor />} />
              <Route path="/staff/cms/about" element={<AboutEditor />} />
            </Route>
            <Route element={<RoleRoute roles={['admin']} />}>
              <Route path="/staff/user-management" element={<UserManagementPage />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </div>
  );
}
export default AllRoutes;